import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Users,
  Plus,
  Trash,
  UserCircle,
  ShieldCheck,
  EnvelopeSimple,
  Lock,
  Warning,
  Buildings,
  X
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UsersPage = () => {
  const { user, getAuthHeaders, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [togglingRole, setTogglingRole] = useState(null);
  const [roleChangeDialog, setRoleChangeDialog] = useState(null); // {user, newRole}
  const [orgs, setOrgs] = useState([]);
  const [membershipDialog, setMembershipDialog] = useState(null); // {user, selectedIds, homeId}
  const [savingMembership, setSavingMembership] = useState(false);
  const [newUser, setNewUser] = useState({
    name: "",
    email: "",
    role: "user",
    organization_id: "",
  });

  useEffect(() => {
    fetchUsers();
    if (isSuperAdmin) {
      fetchOrgs();
    }
  }, []);

  const fetchOrgs = async () => {
    try {
      const res = await axios.get(`${API}/organizations`, { headers: getAuthHeaders() });
      setOrgs(res.data || []);
      // Default new-user org to caller's own org
      if (res.data?.length && !newUser.organization_id) {
        setNewUser(prev => ({ ...prev, organization_id: user?.organization_id || res.data[0].id }));
      }
    } catch {}
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const openMembershipDialog = async (targetUser) => {
    // Load the user's current memberships
    try {
      const res = await axios.get(`${API}/users/${targetUser.id}/organizations`, {
        headers: getAuthHeaders(),
      });
      const selected = res.data.organizations.map(o => o.id);
      setMembershipDialog({
        user: targetUser,
        selectedIds: selected,
        homeId: res.data.home_organization_id,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load memberships");
    }
  };

  const toggleOrgMembership = (orgId) => {
    if (!membershipDialog) return;
    setMembershipDialog(prev => {
      const isSelected = prev.selectedIds.includes(orgId);
      let next = isSelected
        ? prev.selectedIds.filter(i => i !== orgId)
        : [...prev.selectedIds, orgId];
      // Don't allow removing the home org without picking a new one first
      let homeId = prev.homeId;
      if (!next.includes(homeId)) {
        homeId = next[0] || "";
      }
      return { ...prev, selectedIds: next, homeId };
    });
  };

  const setMembershipHome = (orgId) => {
    setMembershipDialog(prev => prev ? { ...prev, homeId: orgId } : null);
  };

  const handleSaveMembership = async () => {
    if (!membershipDialog) return;
    const { user: target, selectedIds, homeId } = membershipDialog;
    if (selectedIds.length === 0) {
      toast.error("Pick at least one organization");
      return;
    }
    if (!selectedIds.includes(homeId)) {
      toast.error("Home organization must be one of the selected");
      return;
    }
    setSavingMembership(true);
    try {
      await axios.put(
        `${API}/users/${target.id}/organizations`,
        { organization_ids: selectedIds, home_organization_id: homeId },
        { headers: getAuthHeaders() }
      );
      toast.success(`${target.name} now belongs to ${selectedIds.length} organization${selectedIds.length === 1 ? "" : "s"}`);
      setMembershipDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save memberships");
    } finally {
      setSavingMembership(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email) {
      toast.error("Please fill in name and email");
      return;
    }

    setCreating(true);
    try {
      const payload = {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
      };
      if (isSuperAdmin && newUser.organization_id) {
        payload.organization_id = newUser.organization_id;
      }
      const response = await axios.post(`${API}/users`, payload, {
        headers: getAuthHeaders()
      });
      if (response.data.email_sent) {
        toast.success("User created! Login credentials sent to their email.");
      } else {
        toast.success("User created! (Email delivery may be delayed)");
      }
      setDialogOpen(false);
      setNewUser({ name: "", email: "", role: "user", organization_id: user?.organization_id || "" });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!showDeleteModal || deleteConfirmText !== "DELETE") return;

    try {
      await axios.delete(`${API}/users/${showDeleteModal.id}`, {
        headers: getAuthHeaders()
      });
      toast.success("User deleted");
      setShowDeleteModal(null);
      setDeleteConfirmText("");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const requestRoleChange = (u, newRole) => {
    if (!newRole || newRole === u.role) return;
    setRoleChangeDialog({ user: u, newRole });
  };

  const handleToggleRole = async () => {
    if (!roleChangeDialog) return;
    const { user: u, newRole } = roleChangeDialog;
    setTogglingRole(u.id);
    try {
      await axios.patch(`${API}/users/${u.id}/role`, { role: newRole }, {
        headers: getAuthHeaders()
      });
      toast.success(`${u.name} is now ${newRole}`);
      setRoleChangeDialog(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setTogglingRole(null);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
            User Management
          </h1>
          <p className="text-slate-500 mt-1">Manage team members and their access</p>
        </div>
        
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (open) {
            setNewUser({ name: "", email: "", role: "user", organization_id: user?.organization_id || "" });
          }
        }}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#006994] hover:bg-[#005A80] text-white font-bold rounded-sm "
              data-testid="add-user-btn"
            >
              <Plus size={18} className="mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-slate-200">
            <DialogHeader>
              <DialogTitle className="text-slate-900">Add New User</DialogTitle>
              <DialogDescription className="text-slate-500">
                Create a new user account for your team
              </DialogDescription>
            </DialogHeader>
            <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-slate-700">Full Name *</Label>
                <div className="relative">
                  <UserCircle className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    value={newUser.name}
                    onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter full name"
                    className="pl-10 bg-white/50 border-slate-200 text-slate-900"
                    autoComplete="off"
                    data-testid="new-user-name-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Email *</Label>
                <div className="relative">
                  <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                  <Input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="Enter email address"
                    className="pl-10 bg-white/50 border-slate-200 text-slate-900"
                    autoComplete="new-email"
                    data-testid="new-user-email-input"
                  />
                </div>
              </div>
              <div className="p-3 rounded-lg bg-[#006994]/5 border border-[#006994]/20">
                <div className="flex items-center gap-2">
                  <Lock size={16} className="text-[#006994]" />
                  <span className="text-sm text-slate-700">Password auto-generated</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">A random password will be generated and emailed to the user.</p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700">Role</Label>
                <Select value={newUser.role} onValueChange={(v) => setNewUser(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger className="bg-white/50 border-slate-200 text-slate-900" data-testid="new-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="user" className="text-slate-900 hover:bg-slate-100">User</SelectItem>
                    <SelectItem value="admin" className="text-slate-900 hover:bg-slate-100">Admin</SelectItem>
                    {isSuperAdmin && (
                      <SelectItem value="super_admin" className="text-[#006994] hover:bg-slate-100">Super Admin</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {isSuperAdmin && orgs.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-slate-700">Organization</Label>
                  <Select
                    value={newUser.organization_id || ""}
                    onValueChange={(v) => setNewUser(prev => ({ ...prev, organization_id: v }))}
                  >
                    <SelectTrigger className="bg-white/50 border-slate-200 text-slate-900" data-testid="new-user-org-select">
                      <SelectValue placeholder="Select organization" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      {orgs.map(o => (
                        <SelectItem key={o.id} value={o.id} className="text-slate-900 hover:bg-slate-100">{o.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            </form>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-slate-200 text-slate-700"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateUser}
                disabled={creating}
                className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                data-testid="create-user-submit-btn"
              >
                {creating ? "Creating..." : "Create User"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Users List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="glass-card border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
              <Users size={20} className="text-[#006994]" />
              Team Members ({users.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">User</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Email</th>
                    {isSuperAdmin && (
                      <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Organization</th>
                    )}
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Role</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Joined</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b border-slate-200 hover:bg-slate-100/30 transition-colors">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#006994] to-[#0EA5E9] flex items-center justify-center text-white font-bold text-sm">
                            {u.name?.charAt(0) || "U"}
                          </div>
                          <span className="text-sm text-slate-900 font-medium">{u.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">{u.email}</td>
                      {isSuperAdmin && (
                        <td className="py-3 px-4 text-sm text-slate-500">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span>{u.organization_name || "—"}</span>
                            {(u.organization_ids?.length || 0) > 1 && (
                              <span
                                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider bg-[#0EA5E9]/10 text-[#0EA5E9] border border-[#0EA5E9]/20 rounded-full px-2 py-0.5"
                                title={(u.organization_names || []).filter(Boolean).join(", ")}
                              >
                                +{u.organization_ids.length - 1} more
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                      <td className="py-3 px-4">
                        {u.id !== user.id && u.email !== "info@dolphin-digital.com" ? (
                          <Select
                            value={u.role}
                            onValueChange={(newRole) => requestRoleChange(u, newRole)}
                            disabled={togglingRole === u.id}
                          >
                            <SelectTrigger className={`w-32 h-8 text-xs font-medium rounded-full border ${
                              u.role === "super_admin"
                                ? "bg-[#006994]/10 text-[#006994] border-[#006994]/30"
                                : u.role === "admin"
                                ? "bg-[#0EA5E9]/10 text-[#0EA5E9] border-[#0EA5E9]/20"
                                : "bg-slate-100 text-slate-500 border-slate-200"
                            }`} data-testid={`role-select-${u.id}`}>
                              <div className="flex items-center gap-1">
                                {(u.role === "admin" || u.role === "super_admin") && <ShieldCheck size={12} />}
                                <span className="capitalize">{u.role === "super_admin" ? "Super Admin" : u.role}</span>
                              </div>
                            </SelectTrigger>
                            <SelectContent className="bg-white border-slate-200">
                              {isSuperAdmin && (
                                <SelectItem value="super_admin" className="text-[#006994] focus:bg-slate-100 focus:text-[#006994]">
                                  <span className="flex items-center gap-1"><ShieldCheck size={12} /> Super Admin</span>
                                </SelectItem>
                              )}
                              <SelectItem value="admin" className="text-[#0EA5E9] focus:bg-slate-100 focus:text-[#0EA5E9]">
                                <span className="flex items-center gap-1"><ShieldCheck size={12} /> Admin</span>
                              </SelectItem>
                              <SelectItem value="user" className="text-slate-700 focus:bg-slate-100 focus:text-slate-900">
                                User
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            u.role === "super_admin"
                              ? "bg-[#006994]/10 text-[#006994] border border-[#006994]/30"
                              : u.role === "admin"
                              ? "bg-[#0EA5E9]/10 text-[#0EA5E9] border border-[#0EA5E9]/20"
                              : "bg-slate-100 text-slate-500 border border-slate-200"
                          }`}>
                            <ShieldCheck size={12} />
                            {u.role === "super_admin" ? "Super Admin" : u.role}{u.id === user.id ? " (you)" : ""}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">{formatDate(u.created_at)}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1">
                          {isSuperAdmin && u.email !== "info@dolphin-digital.com" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openMembershipDialog(u)}
                              className="text-slate-500 hover:text-[#0EA5E9] hover:bg-[#0EA5E9]/10"
                              title="Manage organizations"
                              data-testid={`manage-orgs-${u.id}`}
                            >
                              <Buildings size={16} />
                            </Button>
                          )}
                          {u.id !== user.id && u.email !== "info@dolphin-digital.com" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setShowDeleteModal(u); setDeleteConfirmText(""); }}
                              className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                              data-testid={`delete-user-${u.id}`}
                            >
                              <Trash size={16} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Role Change Confirmation Modal */}
      {roleChangeDialog && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4" onClick={() => setRoleChangeDialog(null)}>
          <div
            className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
            data-testid="role-change-dialog"
          >
            <div className="p-5 border-b border-slate-200 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#0EA5E9]/10 flex items-center justify-center flex-shrink-0">
                <ShieldCheck size={20} className="text-[#0EA5E9]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Change Role?</h3>
                <p className="text-xs text-slate-500 mt-0.5">This will update the user's access level immediately.</p>
              </div>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-slate-700">
                You are about to change <span className="font-semibold text-slate-900">{roleChangeDialog.user.name}</span>'s role:
              </p>
              <div className="bg-white/60 border border-slate-200 rounded-lg p-4 flex items-center justify-between">
                <div className="text-center flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">From</div>
                  <div className={`inline-flex items-center gap-1 text-sm font-medium ${
                    roleChangeDialog.user.role === "admin" ? "text-[#0EA5E9]" : "text-slate-700"
                  }`}>
                    {roleChangeDialog.user.role === "admin" && <ShieldCheck size={14} />}
                    {roleChangeDialog.user.role}
                  </div>
                </div>
                <div className="text-slate-400 text-xl px-2">→</div>
                <div className="text-center flex-1">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">To</div>
                  <div className={`inline-flex items-center gap-1 text-sm font-medium ${
                    roleChangeDialog.newRole === "admin" ? "text-[#0EA5E9]" : "text-slate-700"
                  }`}>
                    {roleChangeDialog.newRole === "admin" && <ShieldCheck size={14} />}
                    {roleChangeDialog.newRole}
                  </div>
                </div>
              </div>
              {roleChangeDialog.newRole === "admin" && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                  <Warning size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-amber-200 leading-relaxed">
                    Admins can manage users, view all jobs, and access system settings. Only promote trusted teammates.
                  </p>
                </div>
              )}
            </div>
            <div className="p-5 pt-0 flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setRoleChangeDialog(null)}
                className="text-slate-700 hover:bg-slate-100"
                data-testid="role-change-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleToggleRole}
                disabled={togglingRole === roleChangeDialog.user.id}
                className="bg-[#0EA5E9] hover:bg-[#0284C7] text-slate-900 font-bold"
                data-testid="role-change-confirm-btn"
              >
                {togglingRole === roleChangeDialog.user.id ? "Updating..." : "Confirm Change"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete User Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60" onClick={() => setShowDeleteModal(null)}>
          <div 
            className="bg-white border border-slate-200 rounded-lg shadow-2xl w-full max-w-md mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Warning size={24} className="text-red-500" />
                <h3 className="text-lg font-bold text-slate-900">Delete User</h3>
              </div>
              <button onClick={() => setShowDeleteModal(null)} className="text-slate-500 hover:text-slate-900">
                <X size={20} />
              </button>
            </div>
            <p className="text-slate-500 mb-6">
              This action cannot be undone. This will permanently delete the user <strong className="text-slate-900">"{showDeleteModal.name}"</strong> ({showDeleteModal.email}).
            </p>
            <div className="space-y-2 mb-6">
              <label className="text-sm text-slate-500">Type <span className="text-red-500 font-bold">DELETE</span> to confirm:</label>
              <Input
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="bg-white border-slate-200 text-slate-900 focus:border-[#006994] focus:ring-1 focus:ring-[#006994]"
                data-testid="delete-user-confirm-input"
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(null)}
                className="border-slate-200 text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDeleteUser}
                disabled={deleteConfirmText !== "DELETE"}
                className="bg-red-600 hover:bg-red-700 text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="delete-user-confirm-btn"
              >
                Delete User
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Organizations Dialog (super admin) */}
      <Dialog open={!!membershipDialog} onOpenChange={(open) => { if (!open) setMembershipDialog(null); }}>
        <DialogContent className="bg-white border-slate-200 max-w-lg" data-testid="membership-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Buildings size={20} className="text-[#0EA5E9]" />
              Manage Organizations
            </DialogTitle>
            <DialogDescription className="text-slate-500">
              Pick every organization <span className="font-bold text-slate-900">{membershipDialog?.user?.name}</span> can access. The home org is the default when they sign in; they can toggle between the others via the org switcher.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2 max-h-80 overflow-y-auto" data-testid="membership-list">
            {orgs.map(o => {
              const selected = membershipDialog?.selectedIds.includes(o.id);
              const isHome = membershipDialog?.homeId === o.id;
              return (
                <div
                  key={o.id}
                  className={`flex items-center justify-between gap-3 p-3 rounded-lg border transition-colors ${
                    selected ? "bg-slate-100 border-slate-200" : "bg-slate-50 border-slate-200"
                  }`}
                >
                  <label className="flex items-center gap-3 cursor-pointer flex-1">
                    <input
                      type="checkbox"
                      checked={!!selected}
                      onChange={() => toggleOrgMembership(o.id)}
                      className="w-4 h-4 accent-[#006994] cursor-pointer"
                      data-testid={`membership-checkbox-${o.slug}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-900 text-sm">{o.name}</p>
                      <p className="text-xs text-slate-500 font-mono truncate">{o.slug}</p>
                    </div>
                  </label>
                  {selected && (
                    <button
                      type="button"
                      onClick={() => setMembershipHome(o.id)}
                      className={`text-[10px] uppercase tracking-wider rounded px-2 py-1 border transition-colors ${
                        isHome
                          ? "bg-[#006994]/10 text-[#006994] border-[#006994]/30"
                          : "bg-transparent text-slate-500 border-slate-200 hover:text-slate-900 hover:border-slate-400"
                      }`}
                      data-testid={`membership-home-${o.slug}`}
                    >
                      {isHome ? "Home" : "Set home"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setMembershipDialog(null)}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="membership-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveMembership}
              disabled={savingMembership || (membershipDialog?.selectedIds?.length || 0) === 0}
              className="bg-[#0EA5E9] hover:bg-[#0284C7] text-slate-900 font-bold"
              data-testid="membership-save-btn"
            >
              {savingMembership ? "Saving..." : "Save memberships"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersPage;
